import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  ArrowUpRight,
  Building2,
  TowerControl,
  Info,
  PlaneTakeoff,
  Fuel,
  ShieldCheck,
  Briefcase,
  Home,
  TriangleAlert,
  NotebookText,
  Star,
} from '@/components/icons'
import { Card } from './ui/card'
import { Badge } from './ui/badge'
import { MenuItem } from './ui/menu-item'
import { Skeleton } from './ui/skeleton'
import { EmptyState } from './EmptyState'
import { AppLayout } from './AppLayout'
import { TopBarBackIconButton } from './topbar/TopBarBackIconButton'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import { WeatherAttribution } from './WeatherAttribution'
import { PhoneLink, EmailLink } from './ContactLink'
import type { FboRecord } from './FboSection'
import {
  useAirportByIcao,
  useAirportByIcaoStatus,
  useAirportIntel,
  useFbosByAirport,
  type Airport,
} from '@/lib/hooks'
import { useWeather, weatherEmojis, type WeatherData } from '../hooks/useWeather'
import { useCrewGetRegionalSettings } from '../lib/hooks/crew'
import { track, EVENTS } from '@/utils/analytics'

dayjs.extend(relativeTime)

// Shape of an airport_intel row as served by airport.getIntel.
interface AirportIntelRecord {
  icao: string
  iata: string | null
  name: string | null
  city: string | null
  country: string | null
  elevation: number | null
  timezone: string | null
  distanceFromCity: string | null
  customs: boolean | null
  airportOfEntry: string | null
  open24Hours: string | null
  hoursOfOperation: string | null
  controlTowerHours: string | null
  runwayId: string | null
  runwayLength: number | null
  runwayWidth: number | null
  approach: string | null
  pcn: string | null
  fuelType: string | null
  handlingMandatory: boolean | null
  slotsRequired: boolean | null
  ppr: number | null
  towerPhone: string | null
  website: string | null
  landingRights: boolean | null
  userFees: boolean | null
  fireCategory: string | null
  suppliers: Array<{
    name: string
    type: string | null
    isFavourite: boolean
    telephone?: string | null
    email?: string | null
    website?: string | null
  }> | null
  hotels: Array<{
    name: string
    isFavourite: boolean
    telephone?: string | null
    email?: string | null
    website?: string | null
  }> | null
  runways: Array<{
    identifier: string
    lengthFt: number | null
    widthFt: number | null
    surface: string | null
    pcn: string | null
  }> | null
  restrictions: Array<{
    details: string | null
    telephone: string | null
    website: string | null
    email: string | null
  }> | null
  notes: {
    notes: string | null
    pilotNotes: string | null
    countryNotes: string | null
    categoryNotes: string | null
  } | null
  fuelSuppliers: Array<{
    name: string
    fuelType: string | null
    telephone?: string | null
    email?: string | null
  }> | null
  fetchedAt: string
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '') return null
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-sm text-content-primary-subtle shrink-0">{label}</span>
      <span className="text-sm text-content-primary-bold text-right">{value}</span>
    </div>
  )
}

function yesNo(value: boolean | null | undefined): string | null {
  return value == null ? null : value ? 'Yes' : 'No'
}

const LIST_PREVIEW_COUNT = 5

// Caps a list at the first 5 entries with a Show more / Show less toggle.
function ExpandableList<T>({
  items,
  renderItem,
}: {
  items: T[]
  renderItem: (item: T, index: number) => React.ReactNode
}) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? items : items.slice(0, LIST_PREVIEW_COUNT)
  const hiddenCount = items.length - LIST_PREVIEW_COUNT

  return (
    <>
      {visible.map(renderItem)}
      {hiddenCount > 0 && (
        <button
          type="button"
          className="flex items-center px-1 py-2 text-xs font-semibold text-content-brand-bold active:opacity-70"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </button>
      )}
    </>
  )
}

function FavouriteStar({ show }: { show: boolean }) {
  if (!show) return null
  return <Star className="size-4 text-content-brand-bold" />
}

// Phone/Email rows shared by FBO, supplier and restriction entries.
function ContactRows({ phone, email }: { phone?: string | null; email?: string | null }) {
  if (!phone && !email) return null
  return (
    <div className="flex flex-col gap-1 px-1 pb-1 text-sm">
      {phone && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-content-primary-subtle">Phone</span>
          <PhoneLink phone={phone} />
        </div>
      )}
      {email && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-content-primary-subtle">Email</span>
          <EmailLink email={email} />
        </div>
      )}
    </div>
  )
}

function feetWithMeters(lengthFt: number | null, widthFt: number | null): string | null {
  if (!lengthFt) return null
  const fmt = (n: number) => n.toLocaleString('en-US')
  const m = (ft: number) => Math.round(ft * 0.3048)
  if (widthFt) {
    return `${fmt(lengthFt)} ft × ${fmt(widthFt)} ft (${fmt(m(lengthFt))} × ${fmt(m(widthFt))} m)`
  }
  return `${fmt(lengthFt)} ft (${fmt(m(lengthFt))} m)`
}

// Minute-ticking local time at the airport. State lives here so only the
// clock re-renders each minute.
function LiveClock({ timezone }: { timezone?: string | null }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined
    // Align to the next minute boundary, then tick every minute.
    const timeout = setTimeout(() => {
      setNow(Date.now())
      interval = setInterval(() => setNow(Date.now()), 60_000)
    }, 60_000 - (Date.now() % 60_000))
    return () => {
      clearTimeout(timeout)
      if (interval) clearInterval(interval)
    }
  }, [])
  if (!timezone) return null
  // Guard against non-IANA values (e.g. stale intel rows cached before the
  // Leon timezone fix) — a bad zone must not crash the whole screen.
  try {
    return <span>{dayjs(now).tz(timezone).format('HH:mm')}</span>
  } catch {
    return null
  }
}

// "partly-cloudy-day" → "Partly Cloudy"
function formatCondition(condition: string): string {
  return condition
    .replace(/-(day|night)$/, '')
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// Local time + current weather header card (airport drawer design).
// Weather is fetched by the parent so the whole screen can gate on it.
function AirportSummaryCard({
  airport,
  timezone,
  weather,
}: {
  airport: Airport
  timezone: string | null
  weather: WeatherData | undefined
}) {
  const { data: regionalSettings } = useCrewGetRegionalSettings() as {
    data?: { temperatureUnit?: string }
  }
  const showCelsius = regionalSettings?.temperatureUnit === 'Celsius'
  const toDisplay = (celsius: number) =>
    showCelsius ? `${Math.round(celsius)}°C` : `${Math.round((celsius * 9) / 5 + 32)}°F`

  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col">
          <p className="text-xs font-semibold text-content-primary-subtle">Local Time</p>
          <p className="text-xl font-bold tracking-[-0.5px] text-content-primary-bold">
            {timezone ? <LiveClock timezone={timezone} /> : '—'}
          </p>
          {airport.municipality && (
            <p className="text-xs font-semibold text-content-primary-subtle">
              {airport.municipality}
            </p>
          )}
        </div>
        {weather && (
          <div className="flex flex-col items-end">
            <p className="text-[32px] leading-9">{weatherEmojis[weather.weatherCondition]}</p>
            <p className="text-xs font-semibold text-content-primary-subtle">
              {formatCondition(weather.weatherCondition)}
            </p>
            <p className="text-[26px] font-bold leading-8 text-content-primary-bold">
              {toDisplay(weather.temperature)}
            </p>
            <p className="text-xs font-semibold text-content-primary-subtle">
              Feels like {toDisplay(weather.apparentTemperature)}
            </p>
          </div>
        )}
      </div>
      {weather && <WeatherAttribution />}
    </Card>
  )
}

// In-card section header per the airport drawer design: round brand icon chip + title.
function SectionHeader({
  icon,
  title,
  count,
}: {
  icon: React.ReactNode
  title: string
  count?: number
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-background-brand-subtle">
        {icon}
      </span>
      <p className="text-base font-semibold tracking-[-0.5px] text-content-primary-bold">{title}</p>
      {typeof count === 'number' && count > 0 && (
        <p className="text-base font-semibold text-content-brand-bold">{count}</p>
      )}
    </div>
  )
}

interface AirportDetailContentProps {
  icao: string
  /** Render the FBO & Handling list (route page). The airport drawer omits it —
   * FBO selection lives in the flight form there. */
  showFbos?: boolean
}

// Airport intel sections shared by the /airport/$icao page and AirportDetailDrawer.
export function AirportDetailContent({ icao, showFbos = true }: AirportDetailContentProps) {
  const { airport, isLoading: airportLoading } = useAirportByIcaoStatus(icao)
  const { data: intelData, isPending: intelPending } = useAirportIntel(icao)
  const intel = (intelData ?? null) as AirportIntelRecord | null

  const { data: fboData, isLoading: fbosLoading } = useFbosByAirport(
    showFbos ? icao : undefined
  )
  const fbos = (fboData ?? undefined) as FboRecord[] | undefined

  // Weather lives here (not in the summary card) so the whole screen can wait
  // for it — sections render together instead of popping in one by one.
  const weatherEnabled = airport?.latitudeDeg != null && airport?.longitudeDeg != null
  const weatherQuery = useWeather({
    latitude: airport?.latitudeDeg ?? 0,
    longitude: airport?.longitudeDeg ?? 0,
    enabled: weatherEnabled,
  })
  const weatherSettled = !weatherEnabled || weatherQuery.isSuccess || weatherQuery.isError

  const timezone = airport?.timezone ?? intel?.timezone ?? null

  // Single gate: local DB row, intel, weather and (on the page) FBOs must all
  // settle before any content shows.
  const isInitialLoading = airportLoading || intelPending || !weatherSettled || fbosLoading
  const airportUnknown = !isInitialLoading && !intel && !airport

  const operatingHours =
    intel?.hoursOfOperation ?? (intel?.open24Hours === 'Y' ? 'H24' : null)

  // Directory lists. "fuelSupplierByLocation" is Leon's full supplier
  // directory; fuel-typed entries + suppliers derived from Leon fuel prices
  // feed the Fuel Suppliers section.
  const suppliers = intel?.suppliers ?? []
  const fuelSuppliers = [
    ...(intel?.fuelSuppliers ?? []).map((s) => ({
      name: s.name,
      type: s.fuelType,
      isFavourite: false,
      telephone: s.telephone ?? null,
      email: s.email ?? null,
    })),
    ...suppliers.filter((s) => s.type && /fuel/i.test(s.type)),
  ]
  // All paired runways (aviowiki); pre-runways rows fall back to the scalars.
  const runways =
    intel?.runways ??
    (intel?.runwayId || intel?.runwayLength
      ? [
          {
            identifier: intel?.runwayId ?? '—',
            lengthFt: intel?.runwayLength ?? null,
            widthFt: intel?.runwayWidth ?? null,
            surface: null,
            pcn: intel?.pcn ?? null,
          },
        ]
      : [])
  const otherSuppliers = suppliers.filter((s) => !(s.type && /fuel/i.test(s.type)))
  const hotels = intel?.hotels ?? []
  const restrictions = (intel?.restrictions ?? []).filter(
    (r) => r.details || r.telephone || r.email || r.website
  )
  const noteEntries: Array<[string, string]> = intel?.notes
    ? (
        [
          ['Notes', intel.notes.notes],
          ['Country Notes', intel.notes.countryNotes],
          ['Category Notes', intel.notes.categoryNotes],
        ] as Array<[string, string | null]>
      ).filter((e): e is [string, string] => !!e[1])
    : []

  if (airportUnknown) {
    return (
      <EmptyState
        icon={TowerControl}
        title="Airport not found"
        description={`No information on file for ${icao}.`}
      />
    )
  }

  if (isInitialLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-44 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 pb-8">
      {/* Local time + weather (client-side; lat/lng from the bundled airport db) */}
      {airport && (
        <AirportSummaryCard airport={airport} timezone={timezone} weather={weatherQuery.data} />
      )}

      {/* Overview */}
      <Card className="flex flex-col gap-3 p-4">
        <SectionHeader
          icon={<Info className="size-4 text-content-brand-bold" />}
          title="Overview"
        />
        <InfoRow label="Airport Name" value={intel?.name ?? airport?.name} />
        <InfoRow
          label="IATA / ICAO"
          value={`${intel?.iata ?? airport?.iataCode ?? '—'} / ${icao}`}
        />
        <InfoRow
          label="Elevation"
          value={intel?.elevation != null ? `${intel.elevation.toLocaleString('en-US')} ft` : null}
        />
        <InfoRow label="Local Time" value={timezone ? <LiveClock timezone={timezone} /> : null} />
        <InfoRow label="Operating Hours" value={operatingHours} />
        <InfoRow label="Control Tower" value={intel?.controlTowerHours} />
        <InfoRow label="Distance to City" value={intel?.distanceFromCity} />
        <InfoRow
          label="Website"
          value={
            intel?.website ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-content-brand-bold underline"
                onClick={() =>
                  window.open(
                    intel.website!.startsWith('http') ? intel.website! : `https://${intel.website}`,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              >
                {intel.website}
                <ArrowUpRight className="size-3.5 shrink-0" />
              </button>
            ) : null
          }
        />
      </Card>

      {/* Suppliers (car hire, caterers, charter, ...) */}
      {otherSuppliers.length > 0 && (
        <Card className="flex flex-col gap-3 p-4">
          <SectionHeader
            icon={<Briefcase className="size-4 text-content-brand-bold" />}
            title="Suppliers"
            count={otherSuppliers.length}
          />
          <ExpandableList
            key={icao}
            items={otherSuppliers}
            renderItem={(s, index) => (
              <div key={s.name} className="flex flex-col gap-1">
                {index > 0 && <div className="h-px bg-border-L1" />}
                <MenuItem
                  size="large"
                  type="button"
                  className="px-1 pointer-events-none"
                  secondaryText={s.type ?? undefined}
                  rightIcon={<FavouriteStar show={s.isFavourite} />}
                >
                  {s.name}
                </MenuItem>
                <ContactRows phone={s.telephone} email={s.email} />
              </div>
            )}
          />
        </Card>
      )}

      {/* Hotels */}
      {hotels.length > 0 && (
        <Card className="flex flex-col gap-3 p-4">
          <SectionHeader
            icon={<Home className="size-4 text-content-brand-bold" />}
            title="Hotels"
            count={hotels.length}
          />
          <ExpandableList
            key={icao}
            items={hotels}
            renderItem={(h, index) => (
              <div key={h.name} className="flex flex-col gap-1">
                {index > 0 && <div className="h-px bg-border-L1" />}
                <MenuItem
                  size="large"
                  type="button"
                  className="px-1 pointer-events-none"
                  rightIcon={<FavouriteStar show={h.isFavourite} />}
                >
                  {h.name}
                </MenuItem>
                <ContactRows phone={h.telephone} email={h.email} />
              </div>
            )}
          />
        </Card>
      )}

      {/* Customs & Immigration */}
      {intel && (
        <Card className="flex flex-col gap-3 p-4">
          <SectionHeader
            icon={<ShieldCheck className="size-4 text-content-brand-bold" />}
            title="Customs and Immigration"
          />
          <InfoRow label="Customs" value={yesNo(intel.customs)} />
          <InfoRow label="Airport of Entry" value={intel.airportOfEntry} />
          <InfoRow label="Slots Required" value={yesNo(intel.slotsRequired)} />
          <InfoRow label="PPR Required" value={intel.ppr != null ? yesNo(!!intel.ppr) : null} />
          <InfoRow label="Handling Mandatory" value={yesNo(intel.handlingMandatory)} />
          <InfoRow label="Landing Rights" value={yesNo(intel.landingRights)} />
          <InfoRow label="User Fees" value={yesNo(intel.userFees)} />
          <InfoRow
            label="Fire Category"
            value={intel.fireCategory ? `CAT ${intel.fireCategory}` : null}
          />
        </Card>
      )}

      {/* Runways */}
      {runways.length > 0 && (
        <Card className="flex flex-col gap-3 p-4">
          <SectionHeader
            icon={<PlaneTakeoff className="size-4 text-content-brand-bold" />}
            title="Runway Information"
            count={runways.length > 1 ? runways.length : undefined}
          />
          {runways.map((runway, index) => (
            <div key={runway.identifier} className="flex flex-col gap-2">
              {index > 0 && <div className="h-px bg-border-L1" />}
              <InfoRow label="Runway" value={runway.identifier} />
              <InfoRow
                label="Dimensions"
                value={feetWithMeters(runway.lengthFt, runway.widthFt)}
              />
              <InfoRow label="Surface" value={runway.surface} />
              <InfoRow label="PCN" value={runway.pcn} />
            </div>
          ))}
          {intel?.approach && <InfoRow label="Approach" value={intel.approach} />}
        </Card>
      )}

      {/* FBO & Handling (route page only — the flight form has its own FBO input) */}
      {showFbos && fbos && fbos.length > 0 && (
        <Card className="flex flex-col gap-3 p-4">
          <SectionHeader
            icon={<Building2 className="size-4 text-content-brand-bold" />}
            title="FBO & Handling"
            count={fbos.length}
          />
          <ExpandableList
            key={icao}
            items={fbos}
            renderItem={(fbo, index) => (
              <div key={fbo.id} className="flex flex-col gap-1">
                {index > 0 && <div className="h-px bg-border-L1" />}
                <MenuItem
                  size="large"
                  type="button"
                  className="px-1 pointer-events-none"
                  secondaryText={
                    [fbo.vhfFrequency && `${fbo.vhfFrequency} MHz`, 'FBO']
                      .filter(Boolean)
                      .join(' · ')
                  }
                >
                  {fbo.name}
                </MenuItem>
                <ContactRows phone={fbo.phone} email={fbo.email} />
              </div>
            )}
          />
        </Card>
      )}

      {/* Fuel */}
      {intel?.fuelType && (
        <Card className="flex flex-col gap-3 p-4">
          <SectionHeader
            icon={<Fuel className="size-4 text-content-brand-bold" />}
            title="Fuel"
          />
          <div className="flex flex-wrap items-center gap-2">
            {/* aviowiki fuelsAvailable list, stored comma-joined */}
            {intel.fuelType.split(', ').map((fuel) => (
              <Badge key={fuel} variant="secondary">
                {fuel}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Fuel Suppliers (fuel-typed entries of Leon's supplier directory) */}
      {fuelSuppliers.length > 0 && (
        <Card className="flex flex-col gap-3 p-4">
          <SectionHeader
            icon={<Fuel className="size-4 text-content-brand-bold" />}
            title="Fuel Suppliers"
            count={fuelSuppliers.length}
          />
          <ExpandableList
            key={icao}
            items={fuelSuppliers}
            renderItem={(s, index) => (
              <div key={s.name} className="flex flex-col gap-1">
                {index > 0 && <div className="h-px bg-border-L1" />}
                <MenuItem
                  size="large"
                  type="button"
                  className="px-1 pointer-events-none"
                  secondaryText={s.type ?? undefined}
                  rightIcon={<FavouriteStar show={s.isFavourite} />}
                >
                  {s.name}
                </MenuItem>
                <ContactRows phone={s.telephone} email={s.email} />
              </div>
            )}
          />
        </Card>
      )}

      {/* Restrictions */}
      {restrictions.length > 0 && (
        <Card className="flex flex-col gap-3 p-4">
          <SectionHeader
            icon={<TriangleAlert className="size-4 text-content-brand-bold" />}
            title="Restrictions"
            count={restrictions.length}
          />
          <ExpandableList
            key={icao}
            items={restrictions}
            renderItem={(r, index) => (
              <div key={index} className="flex flex-col gap-1">
                {index > 0 && <div className="h-px bg-border-L1" />}
                {r.details && (
                  <p className="text-sm font-medium text-content-primary-bold whitespace-pre-wrap">
                    {r.details}
                  </p>
                )}
                <div className="flex flex-col gap-1 text-sm">
                  {r.telephone && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-content-primary-subtle">Phone</span>
                      <PhoneLink phone={r.telephone} />
                    </div>
                  )}
                  {r.email && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-content-primary-subtle">Email</span>
                      <EmailLink email={r.email} />
                    </div>
                  )}
                  {r.website && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-content-primary-subtle">Website</span>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-content-brand-bold underline"
                        onClick={() =>
                          window.open(
                            r.website!.startsWith('http') ? r.website! : `https://${r.website}`,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                      >
                        {r.website}
                        <ArrowUpRight className="size-3.5 shrink-0" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          />
        </Card>
      )}

      {/* Information (operator airport notes) */}
      {noteEntries.length > 0 && (
        <Card className="flex flex-col gap-3 p-4">
          <SectionHeader
            icon={<NotebookText className="size-4 text-content-brand-bold" />}
            title="Information"
          />
          {noteEntries.map(([label, text]) => (
            <div key={label} className="flex flex-col gap-1">
              <p className="text-xs font-medium text-content-primary-subtle">{label}</p>
              <p className="text-sm text-content-primary-bold whitespace-pre-wrap">{text}</p>
            </div>
          ))}
        </Card>
      )}

      {/* Intel freshness (data refreshes from Leon weekly) */}
      {intel?.fetchedAt && (
        <p className="text-xs text-content-primary-subtle text-center">
          Updated {dayjs(intel.fetchedAt).fromNow()}
        </p>
      )}
    </div>
  )
}

interface AirportDetailViewProps {
  icao: string
}

export function AirportDetailView({ icao }: AirportDetailViewProps) {
  const isDesktop = useIsDesktop()
  const airport = useAirportByIcao(icao)

  useEffect(() => {
    track(EVENTS.AIRPORT_DETAIL_VIEWED, { icao })
  }, [icao])

  const title = airport?.name ? `${icao} – ${airport.name}` : icao

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader title={title} />
        <div className="max-w-2xl">
          <AirportDetailContent icao={icao} />
        </div>
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackIconButton title={title} showBackButton />}>
      <AirportDetailContent icao={icao} />
    </AppLayout>
  )
}
